import { LoginOutput } from 'dtos/users/LoginOutput';
import { UserOutput } from 'dtos/users/UserOutput';
import { Role } from 'dtos/users/common';
import { makeAutoObservable, runInAction } from 'mobx';
import { identifyFeatureFlaggerUser } from '@bowery-valuation/feature-flagger-client';
import { launchDarklyClientSideId, serverUri } from 'utils/config';

import { nullsToUndefined } from 'utils/nullsToUndefined';

export const tokenStorageKey = 'token';

export type LoginStatus = 'unknown' | 'loading' | 'valid' | 'invalid';

export type AuthEventAction = 'manualLogout' | 'forcedLogout' | 'login';

export class AuthEvent extends Event {
  static eventType = 'auth';
  action: AuthEventAction;
  constructor(action: AuthEventAction) {
    super(AuthEvent.eventType);
    this.action = action;
  }
}

export class AuthStore {
  readonly events = new EventTarget();

  accessToken?: string;
  currentUser?: UserOutput;
  featureFlags?: any;
  featureFlagsPromise?: Promise<void> | undefined;
  status: LoginStatus = 'unknown';

  constructor() {
    this.accessToken = window.localStorage.getItem(tokenStorageKey) || undefined;
    makeAutoObservable(this, {}, { autoBind: true });
  }

  handleLoginResponse = async (output: LoginOutput) => {
    const { access_token, user } = output;
    window.localStorage.setItem(tokenStorageKey, access_token);
    await this.identifyWithExternalSystems(user);
    runInAction(async () => {
      this.accessToken = access_token;
      this.status = 'valid';
      this.currentUser = user;
      this.events.dispatchEvent(new AuthEvent('login')); // triggers redirect
      await this.setFeatureFlagValues();
    });
  };

  checkAuth = async () => {
    try {
      this.status = 'loading';
      const response = await fetch(`${serverUri}/user`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(this.authHeader && { Authorization: this.authHeader }),
        },
      });
      if (!response.ok) throw new Error('Not logged in');
      const user: UserOutput = nullsToUndefined(await response.json());
      await this.identifyWithExternalSystems(user);
      runInAction(async () => {
        this.status = 'valid';
        this.currentUser = user;
        await this.setFeatureFlagValues();
      });
    } catch (err) {
      window.localStorage.removeItem(tokenStorageKey);
      runInAction(() => {
        this.accessToken = undefined;
        this.status = 'invalid';
        this.currentUser = undefined;
      });
      await this.unidentifyWithExternalSystems();
    }
  };

  setFeatureFlagValues = async () => {
    try {
      const email = this.currentUser?.email;
      const response = await fetch(`${serverUri}/api/feature-flags/all/${email}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(this.authHeader && { Authorization: this.authHeader }),
        },
      });
      const featureFlags = await response.json();
      this.featureFlags = featureFlags || {};
    } catch (error) {
      console.error('Error setting feature flags', error);
      this.featureFlags = {};
    } finally {
      this.featureFlagsPromise = undefined;
    }
  };

  ensureFeatureFlagsLoaded = async () => {
    if (!this.featureFlags && !this.featureFlagsPromise) {
      this.featureFlagsPromise = this.setFeatureFlagValues();
    }

    return this.featureFlagsPromise;
  };

  getFeatureFlagValueForUser = (featureFlagName: string) => {
    if (this.featureFlags && this.featureFlags[featureFlagName]) {
      return this.featureFlags[featureFlagName];
    } else {
      return false;
    }
  };

  async logout() {
    window.localStorage.removeItem(tokenStorageKey);
    this.accessToken = undefined;
    this.status = 'invalid';
    this.currentUser = undefined;
    this.featureFlags = {};
    await this.unidentifyWithExternalSystems();
    this.events.dispatchEvent(new AuthEvent('manualLogout')); // triggers redirect
  }

  async forceLogout() {
    window.localStorage.removeItem(tokenStorageKey);
    this.accessToken = undefined;
    this.status = 'invalid';
    this.currentUser = undefined;
    this.featureFlags = {};
    await this.unidentifyWithExternalSystems();
    this.events.dispatchEvent(new AuthEvent('forcedLogout')); // triggers redirect
  }

  private async identifyWithExternalSystems(user: UserOutput) {
    const { username } = user;

    if (launchDarklyClientSideId) {
      await identifyFeatureFlaggerUser(username);
    }
  }

  private async unidentifyWithExternalSystems() {
    if (launchDarklyClientSideId) {
      // eslint-disable-next-line no-restricted-syntax
      await identifyFeatureFlaggerUser(null);
    }
  }

  async fetchWithAuth(input: RequestInfo | URL, init: RequestInit = {}): Promise<Response> {
    if (this.status === 'valid') {
      init.headers = {
        ...init.headers,
        ...(this.authHeader && { Authorization: this.authHeader }),
      };
    }
    const response = await fetch(input, init);
    this.checkResponse(response);
    return response;
  }

  checkResponse(response: Response) {
    // 401 indicates an actual session expiration or that the user doesn't have required roles assigned (has become disabled)
    if (this.status === 'valid' && response.status === 401) {
      this.forceLogout();
    }
  }

  get authHeader(): string | undefined {
    return this.accessToken ? `Bearer ${this.accessToken}` : undefined;
  }

  setStatus(status: LoginStatus) {
    this.status = status;
  }

  get isLoggedIn() {
    return this.status === 'valid';
  }

  get isInternal() {
    return this.isLoggedIn && this.currentUser?.roles.includes(Role.INTERNAL);
  }

  get isAdmin() {
    return this.isLoggedIn && this.currentUser?.roles.includes(Role.ADMIN);
  }

  updateCurrentUser(user: UserOutput) {
    if (this.currentUser) {
      this.currentUser = user;
    }
  }

  setEmailConfirmed(confirmed: boolean) {
    if (this.currentUser) {
      this.currentUser = {
        ...this.currentUser,
        emailConfirmed: confirmed,
      };
    }
  }
}
